import { useCallback, useEffect, useRef, useState } from 'react'

/** Zone bounding rect expressed as fractions of the container (0.0–1.0). */
export interface ZoneRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ZoneGeometryResult {
  geometry: Record<string, ZoneRect>;
  containerWidth: number;
  containerHeight: number;
}

/**
 * Measures each SVG zone group inside `containerRef` and returns their
 * positions normalised to the container's own bounding rect, plus the
 * container's pixel dimensions.
 *
 * Re-measures whenever the container is resized (ResizeObserver).
 * Measurement is deferred to the next animation frame to avoid
 * forced-layout thrash during rapid resizes.
 * Returns zero dimensions and an empty geometry map until the SVG has mounted.
 */
export function useZoneGeometry (
  containerRef: React.RefObject<HTMLElement>
): ZoneGeometryResult {
  const [result, setResult] = useState<ZoneGeometryResult>({
    geometry: {},
    containerWidth: 0,
    containerHeight: 0
  })

  const rafHandle = useRef<number>(0)

  const measure = useCallback(() => {
    const el = containerRef.current
    if (!el) return

    const containerRect = el.getBoundingClientRect()
    if (containerRect.width === 0 || containerRect.height === 0) return

    const geometry: Record<string, ZoneRect> = {}
    el.querySelectorAll<SVGGElement>('g[data-zone]').forEach((g) => {
      const zone = g.getAttribute('data-zone')
      if (!zone) return
      const r = g.getBoundingClientRect()
      geometry[zone] = {
        x: (r.left - containerRect.left) / containerRect.width,
        y: (r.top - containerRect.top) / containerRect.height,
        w: r.width / containerRect.width,
        h: r.height / containerRect.height
      }
    })

    setResult({
      geometry,
      containerWidth: containerRect.width,
      containerHeight: containerRect.height
    })
  }, [containerRef])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    measure()

    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafHandle.current)
      rafHandle.current = requestAnimationFrame(measure)
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      cancelAnimationFrame(rafHandle.current)
    }
  }, [containerRef, measure])

  return result
}
